import {
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  signal,
  Signal,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { MatStepper } from '@angular/material/stepper';
import { ActivatedRoute } from '@angular/router';
import { CRMIntegrationsService } from '../../services/crm-integrations.service';
import { publicMethods } from '../../globals';
import { concatMap, tap } from 'rxjs/operators';
import { combineLatest, forkJoin } from 'rxjs';
import { ZintModalConfig, ZintModalContent } from '../../classes/types';
import { crmHelp } from '../../classes/crm-help';
import { CommunicationService } from '../../services/communication.service';

@Component({
  selector: 'crm',
  templateUrl: './crm.component.html',
  styleUrl: './crm.component.css',
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class CrmComponent {
  @ViewChild('stepper') stepper: MatStepper;
  canConfigureCRM = false;
  crmType = '';
  currentStepIndex: number = 0;
  crmObjectID = computed(() => {
    return this.crmService.configCRMSelectedObjectId();
  });
  existingObjectId: number = null;
  constructor(
    private cdr: ChangeDetectorRef,
    public crmService: CRMIntegrationsService,
    public pubMethods: publicMethods,
    private commService: CommunicationService,
    private route: ActivatedRoute
  ) {
    document.title = 'CRM Wizard';
    effect(() => {
      const currentValue = this.crmObjectID();
      if (this.existingObjectId !== null && currentValue) {
        this.getCRMConfigSteps(true);
        this.getCompanyMatcherStatus();
      }
      this.existingObjectId = currentValue;
    });
  }

  isLoading = this.crmService.isLoading;

  configStepsData: Record<string, any> = {};
  configWizardCompleted = signal(false);
  isNextBtnDisabled: Signal<Record<string, boolean>>;
  companyMatcherStatus = signal(null);

  crmHelp = crmHelp;
  showHelpModal = false;
  stepHelpModalConfig: ZintModalConfig;
  stepHelpModalContent: ZintModalContent = {};

  ngOnInit(): void {
    this.crmType = this.route.snapshot.paramMap.get('crmType');

    this.getCRMConfigSteps();
  }

  getCRMConfigSteps(resetAllFollowingSteps = false): void {
    combineLatest([
      this.commService.getIsParentOrgSuperUserMessage(),
      this.commService.getIsSuperUserMessage(),
    ])
      .pipe(
        tap(([isParentSuperuser, isOrganisationSuperuser]) => {
          this.canConfigureCRM =
            isParentSuperuser ||
            (this.crmType === 'dynamics' && isOrganisationSuperuser);
        }),
        concatMap(() => {
          return this.crmService.getConfigsForCRM(this.crmType);
        }),
        tap(data => {
          if (resetAllFollowingSteps) {
            this.deleteNonCompletedSteps(data.steps);
            return;
          }

          this.configStepsData = data;
          if (!this.canConfigureCRM) {
            const { steps } = this.configStepsData;
            const nonSuperSteps = steps.filter(
              step => step.superuserOnly === false
            );
            this.configStepsData = {
              ...this.configStepsData,
              steps: nonSuperSteps,
            };
          }

          this.configWizardCompleted.update(() =>
            this.configStepsData.steps?.every(step =>
              this.canConfigureCRM ? step.isCompleted : step.isReady
            )
          );

          this.isNextBtnDisabled = computed(() => {
            /* signal that derives from the readyState of each step
             * and flip it to determine the next btn disabled state
             */
            const readyStates = this.crmService.wizardStepsReadyState();

            if (!readyStates) {
              return {};
            }

            const flippedState = {};
            Object.entries(readyStates).forEach(([k, v]) => {
              flippedState[k] = !v;
            });
            return flippedState;
          });
        })
      )
      .subscribe();
  }

  getCurrentStep(): number {
    return (
      this.crmService.specificConfigWizardStep() ??
      this.configStepsData.steps?.findLastIndex(
        step => step.isCompleted === true
      ) + 1
    );
  }

  // for superusers
  setStepCompletedState(stepData: Record<string, any>, index: number): void {
    const { stepType, isCompleted } = stepData;
    const { steps } = this.configStepsData;

    const isLastIndex = index === steps.length - 1;
    if (!isLastIndex && isCompleted && steps[index].isReady) {
      this.stepper.next();
      return;
    }

    this.crmService.markConfigStepCompleted(stepType).subscribe(data => {
      if (data) {
        if (data.isCompleted === true) {
          steps[index].isCompleted = true;
          if (isLastIndex) {
            this.configWizardCompleted.set(true);
            this.crmService.specificConfigWizardStep.set(null);
          }

          if (!isLastIndex) {
            this.cdr.detectChanges();
            this.stepper.next();
          }
        } else if (data.isCompleted === false) {
          steps[index].isCompleted = false;
          this.cdr.detectChanges();
        }
      }
    });
  }

  //for non-superusers, we use isReady instead of isCompleted
  updateWizardCompletedStateForNonSU(isReadyEvent: boolean): void {
    if (this.canConfigureCRM) return;
    this.configWizardCompleted.set(isReadyEvent);
  }

  showOptionalLabel(stepType: string): string {
    const compulsorySteps = ['auth', 'object_type', 'lookup_field'];
    return !compulsorySteps.includes(stepType) ? '(Optional)' : '';
  }

  showSummary(): void {
    this.configWizardCompleted.set(true);
    this.crmService.specificConfigWizardStep.set(null);
  }

  deleteNonCompletedSteps(stepsData: Record<string, any>[]): void {
    //NB: run when the objectType changes
    const exclusions = ['auth', 'object_type'];
    const stepsToDelete = stepsData.filter(
      step => step.isCompleted && !exclusions.includes(step.stepType)
    );
    const deleteSteps$ = stepsToDelete.map(step =>
      this.crmService.deleteConfigStep(step.stepType)
    );

    // once deletes are done get configSteps again to get fresh steps
    forkJoin(deleteSteps$).subscribe(() => {
      this.getCRMConfigSteps();
      const objectSelectStep = this.configStepsData.steps.findIndex(
        step => step.stepType === 'object_type'
      );
      this.crmService.specificConfigWizardStep.set(objectSelectStep);
      this.crmService.cachedCRMObjectFields$ = null;
    });
  }

  goToAuthStep(): void {
    const authStepIndex = this.configStepsData.steps.findIndex(
      step => (step.stepType = 'auth')
    );
    this.crmService.specificConfigWizardStep.set(authStepIndex);
  }

  refreshDynamicsConnection(): void {
    const authStepIndex = this.configStepsData.steps.findIndex(
      step => step.stepType === 'auth'
    );
    this.crmService.specificConfigWizardStep.set(authStepIndex);
    this.getCRMConfigSteps();
  }

  toggleHelpModal(stepType: string): void {
    this.stepHelpModalConfig = { vCentered: true, name: stepType, size: 'lg' };

    this.stepHelpModalContent = {
      plainText: this.crmHelp[stepType].plainText,
      htmlElement: this.crmHelp[stepType].htmlElement as any as HTMLElement,
    };

    this.showHelpModal = true;
    this.cdr.detectChanges();
    this.pubMethods.triggerZintModal(stepType);
  }

  getCompanyMatcherStatus(): void {
    const objectId = this.crmObjectID();
    if (objectId) {
      this.crmService.getCompanyMatcherStatus(objectId).subscribe({
        next: (status) => {
          this.companyMatcherStatus.set(status);
        },
        error: (error) => {
          console.error('Error fetching company matcher status:', error);
          this.companyMatcherStatus.set(null);
        }
      });
    }
  }
}
